import { last } from 'lodash';
import { WorkSheet, utils } from 'xlsx-js-style';
import {
  getConversionFactorValue,
  hasConversionFactor,
} from '@shared/helpers/conversion-factors.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import {
  getGenericProduct,
  getOriginalProductId,
  getProductCategories,
  getProductName,
  isNodonProductId,
} from '@shared/helpers/product.helpers';
import {
  forEachElement,
  getPathToElement,
  isElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  Baseline,
  IProductElement,
  ProductElementOverrideTarget,
  type ProjectMarketEstimates,
} from '@shared/models/project.model';
import {
  Results,
  ResultsRecord,
  type CO2eUnit,
  type CostUnit,
} from '@shared/models/unit.model';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import {
  getElementsInProposal,
  isElementActiveInProposal,
} from '@shared/helpers/proposal.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import {
  getEPDProductId,
  getGenericProductId,
  getProductDescription,
  getProductElementData,
  getProductOverride,
} from '@shared/helpers/product-element.helpers';
import { ProductSources } from '@shared/models/product.model';
import {
  getSbefCodeFromElementOrRecipe,
  getSbefGroupCode,
  getSbefLabel,
} from '@shared/helpers/sbef.helpers';
import { SbefGroupCode, type SbefCode } from '@shared/construction/sbef.model';
import { Lifecycle, lifecycles } from '@shared/models/lifecycles.model';
import {
  filterResultsByLifecycles,
  getCO2eUnitsFromLifecycle,
  getCO2eUnitStageLabel,
  getCostUnitsFromLifecycle,
  getCostUnitStageLabel,
} from '@shared/helpers/lifecycles.helpers';
import {
  getElementProductCategory,
  isProductCategoryElement,
  isSystemCategoryElement,
} from '@shared/helpers/element-category.helpers';
import {
  createMarketEstimatesSnapshot,
  getProductEstimates,
  percentileToFactor,
} from '@shared/helpers/product-market-estimates.helpers';
import { getProductClimateImpactLevelNumber } from '@shared/helpers/products/product-climate-impact-level.helpers';
import {
  EXPORT_BUILDING_LEVEL_ELEMENT_LABEL,
  EXPORT_BUILDING_LEVEL_RESOURCE_LABEL,
  EXPORT_COLUMN,
  EXPORT_HEADER_FILL_COLOR,
  EXPORT_INTEGER_NUMBER_FORMAT,
  ExportCellValue,
  getExportCo2eStageHeader,
  getExportCostStageHeader,
} from '@client/models/export.model';

/** Excel worksheet tab limit; characters not allowed in tab names (ECMA-376). */
const EXCEL_SHEET_TAB_MAX_LEN = 31;
const INVALID_EXCEL_SHEET_TAB_CHARS = new Set('[]:*?/\\');

/**
 * One CO2e column per CO2e unit rather than per lifecycle, so B1 reports carbonation
 * (B1.1) and refrigerant leakage (B1.2) in separate columns instead of one net figure.
 */
type ExportCo2eStageColumn = {
  lifecycle: Lifecycle;
  /** Column header, e.g. `kgCO2e B1.1`. */
  header: string;
  unit: CO2eUnit;
};

/** One cost column per cost unit; stages without a cost dimension (B1/B7/C) get none. */
type ExportCostStageColumn = {
  lifecycle: Lifecycle;
  /** Column header, e.g. `SEK A1-A3`. */
  header: string;
  unit: CostUnit;
};

export type ExportLifecycleColumns = {
  co2e: readonly ExportCo2eStageColumn[];
  cost: readonly ExportCostStageColumn[];
  /** {@link co2e} then {@link cost}, precomputed so rows do not re-concatenate per element. */
  all: readonly (ExportCo2eStageColumn | ExportCostStageColumn)[];
};

type ExportClimateAdjustment = {
  level: number;
  /** Signed percent vs generic (negative = reduction), e.g. `-30`. */
  percentAdjustment: number;
};

type ExportClimateFields = {
  climateData: string;
  /**
   * Product source for generic/EPD rows (e.g. `Boverket`, `EPD`);
   * adjusted generics use `Boverket (Adjusted Level N)`.
   */
  dataSource: string;
};

/** Fits values up to ~20 318 145 with thousand separators. */
const NUMBER_COLUMN_WIDTH = 13;
const TEXT_COLUMN_WIDTH = 18;
const LONG_TEXT_COLUMN_WIDTH = 22;
const DEFAULT_COLUMN_WIDTH = TEXT_COLUMN_WIDTH;

/**
 * Mass (kg) for material products; energy (kWh) when {@link element.unit} is `kWh`.
 */
export const getExportMassOrEnergyUnit = (
  element: IProductElement,
): 'kg' | 'kWh' => (element.unit === 'kWh' ? 'kWh' : 'kg');

const COLUMN_WIDTH_BY_HEADER: Record<string, number> = {
  // Column A — ~50% wider than default text columns for element names
  [EXPORT_COLUMN.elementName]: Math.round(TEXT_COLUMN_WIDTH * 1.5),
  [EXPORT_COLUMN.resourceName]: TEXT_COLUMN_WIDTH,
  [EXPORT_COLUMN.massKg]: NUMBER_COLUMN_WIDTH,
  [EXPORT_COLUMN.sbefParent]: 10,
  [EXPORT_COLUMN.climateData]: LONG_TEXT_COLUMN_WIDTH,
  [EXPORT_COLUMN.sourceId]: LONG_TEXT_COLUMN_WIDTH,
  [EXPORT_COLUMN.epdDescription]: LONG_TEXT_COLUMN_WIDTH,
  [EXPORT_COLUMN.dataSource]: 28,
  [EXPORT_COLUMN.sbefCode]: TEXT_COLUMN_WIDTH,
  [EXPORT_COLUMN.boverketCategory]: TEXT_COLUMN_WIDTH,
  'Sheet name': TEXT_COLUMN_WIDTH,
  'kgCO2e per m2': NUMBER_COLUMN_WIDTH,
};

/**
 * Preferred column widths keyed by spreadsheet header.
 * Dynamic stage headers (`kgCO2e …` / `SEK …`) use the number width.
 */
const getColumnWidth = (header: string): number => {
  if (header.startsWith('kgCO2e ') || header.startsWith('SEK ')) {
    return NUMBER_COLUMN_WIDTH;
  }
  return COLUMN_WIDTH_BY_HEADER[header] ?? DEFAULT_COLUMN_WIDTH;
};

/**
 * Returns a sanitized, unique worksheet tab name and registers it in {@link usedNames}.
 */
export const allocateExportWorksheetTabName = (
  usedNames: Set<string>,
  rawName: string,
): string => {
  const cleaned = [...rawName]
    .map((ch) => (INVALID_EXCEL_SHEET_TAB_CHARS.has(ch) ? '_' : ch))
    .join('')
    .trim();

  let base = cleaned.slice(0, EXCEL_SHEET_TAB_MAX_LEN);
  if (!base) base = 'Sheet';

  let candidate = base;
  let n = 2;
  while (usedNames.has(candidate)) {
    const suffix = ` (${n})`;
    candidate = `${base.slice(0, EXCEL_SHEET_TAB_MAX_LEN - suffix.length)}${suffix}`;
    n += 1;
  }

  usedNames.add(candidate);
  return candidate;
};

/**
 * Lifecycle stage columns for the selected export scope (canonical lifecycle order).
 * Every CO2e unit of a selected stage gets its own kgCO2e column; cost columns only
 * when the stage has a SEK dimension.
 */
export const getExportLifecycleColumns = (
  selectedLifecycles: Lifecycle[],
): ExportLifecycleColumns => {
  const selectedSet = new Set(selectedLifecycles);
  const selected = lifecycles.filter((lifecycle) => selectedSet.has(lifecycle));
  // Both headers are derived from the unit, never the lifecycle, so a lifecycle that owns
  // several units (B1 → B1.1/B1.2) can never collapse into one shared column.
  const co2e = selected.flatMap((lifecycle) =>
    getCO2eUnitsFromLifecycle(lifecycle).map((unit) => ({
      lifecycle,
      header: getExportCo2eStageHeader(getCO2eUnitStageLabel(unit)),
      unit,
    })),
  );
  const cost = selected.flatMap((lifecycle) =>
    getCostUnitsFromLifecycle(lifecycle).map((unit) => ({
      lifecycle,
      header: getExportCostStageHeader(getCostUnitStageLabel(unit)),
      unit,
    })),
  );
  return { co2e, cost, all: [...co2e, ...cost] };
};

/**
 * Ordered headers for a resource sheet given the selected lifecycle columns.
 */
export const getExportResourceHeaders = (
  lifecycleColumns: ExportLifecycleColumns,
): string[] => [
  EXPORT_COLUMN.elementName,
  EXPORT_COLUMN.resourceName,
  EXPORT_COLUMN.massKg,
  EXPORT_COLUMN.co2eTotal,
  ...lifecycleColumns.co2e.map((column) => column.header),
  EXPORT_COLUMN.costTotal,
  ...lifecycleColumns.cost.map((column) => column.header),
  EXPORT_COLUMN.climateData,
  EXPORT_COLUMN.dataSource,
  EXPORT_COLUMN.sourceId,
  EXPORT_COLUMN.epdDescription,
  EXPORT_COLUMN.sbefParent,
  EXPORT_COLUMN.sbefCode,
  EXPORT_COLUMN.boverketCategory,
];

/**
 * SBEF parent group label for export, e.g. `3. Frame`.
 * Standard 2-digit codes use the first digit; A5 codes 101–105 roll up under Common works (`9`).
 */
export const getExportSbefParent = (code?: SbefCode): string => {
  const group =
    getSbefGroupCode(code) ??
    (code && /^10[1-5]$/.test(code) ? SbefGroupCode.CommonWorks : undefined);
  if (!group) return '';
  return getSbefLabel(group, { fallback: '' });
};

/**
 * Carbon adjustment (level + signed %) when the element has no EPD but a carbon-level override.
 */
export const getExportClimateAdjustment = (
  element: IProductElement,
  marketEstimates?: ProjectMarketEstimates,
): ExportClimateAdjustment | undefined => {
  if (getEPDProductId(element)) return undefined;

  const scoped = getProductOverride(
    element,
    ProductElementOverrideTarget.CO2E_A1_A3,
  );
  if (!scoped) return undefined;

  const percentile = scoped.override.value;
  const estimates = marketEstimates ?? createMarketEstimatesSnapshot();
  const genericProductId = getGenericProductId(element) ?? element.product_id;
  const categoryId = getElementProductCategory(genericProductId);
  const estimate =
    getProductEstimates(estimates, genericProductId) ?? estimates.defaults;
  const percentAdjustment = Math.round(
    percentileToFactor(percentile, estimate.carbon) * 100,
  );
  const level = getProductClimateImpactLevelNumber(
    categoryId,
    percentAdjustment,
  );
  return { level, percentAdjustment };
};

/**
 * Formats adjusted-generic climate type, e.g. `Boverket (Adjusted Level 3)`.
 */
export const formatExportAdjustedGenericType = (
  adjustment: ExportClimateAdjustment,
): string => `Boverket (Adjusted Level ${adjustment.level})`;

/**
 * Resolves Climate data name and Data source for one product element.
 * Adjusted generics put level into Data source instead of a separate type column.
 */
export const getExportClimateFields = (
  element: IProductElement,
  version: Baseline,
  marketEstimates?: ProjectMarketEstimates,
): ExportClimateFields => {
  const product = version.products[element.product_id];
  const climateRef = getProductElementData(element);
  const climateName = getProductName(
    climateRef.currentId,
    version.products,
    '',
  );
  const epdId = getEPDProductId(element);

  /**
   * Resolves the Data source label from the product record.
   * Custom EPDs surface as `EPD`; other sources keep their product source name.
   */
  const resolveProductDataSource = (): string => {
    if (!product) return '';
    if (product.source === ProductSources.Custom) return 'EPD';
    if (
      product.source === ProductSources.Nodon ||
      isNodonProductId(element.product_id)
    ) {
      return 'Nodon';
    }
    return product.source;
  };

  if (epdId) {
    // Append mapped generic in parentheses after the EPD name
    const mappedGeneric = getProductName(
      getGenericProductId(element),
      version.products,
      '',
    );
    const climateData = mappedGeneric
      ? `${climateName} (${mappedGeneric})`
      : climateName;
    return {
      climateData,
      dataSource: resolveProductDataSource() || 'EPD',
    };
  }

  const adjustment = getExportClimateAdjustment(element, marketEstimates);
  if (adjustment) {
    return {
      climateData: climateName,
      dataSource: formatExportAdjustedGenericType(adjustment),
    };
  }

  return {
    climateData: climateName,
    dataSource: resolveProductDataSource(),
  };
};

/**
 * Rounded factor when the unit exists on {@link factors}; otherwise blank (`null`).
 */
const getOptionalRoundedFactor = (
  factors: Results,
  unit: CO2eUnit | CostUnit | 'kg' | 'kWh' | 'co2e' | 'sek',
): number | null => {
  if (unit === 'co2e' || unit === 'sek' || unit === 'kg') {
    return Math.round(getConversionFactorValue(factors, unit));
  }
  if (!hasConversionFactor(factors, unit)) return null;
  return Math.round(getConversionFactorValue(factors, unit));
};

/**
 * Applies number formats, alignment, header style, filter, and freeze panes.
 * Text is left + vertically centered; numbers are right + vertically centered.
 * No wrap — each resource stays on a single row.
 */
export const applyExportSheetPresentation = (
  sheet: WorkSheet,
  headers: readonly string[],
  options?: { freezePanes?: boolean },
): void => {
  if (!sheet['!ref']) return;
  const range = utils.decode_range(sheet['!ref']);

  for (let col = range.s.c; col <= range.e.c; col++) {
    const headerRef = utils.encode_cell({ r: 0, c: col });
    const headerCell = sheet[headerRef];
    if (!headerCell) continue;
    headerCell.s = {
      ...headerCell.s,
      font: { bold: true },
      fill: {
        patternType: 'solid',
        fgColor: { rgb: EXPORT_HEADER_FILL_COLOR },
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center',
        wrapText: false,
      },
    };
  }

  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = utils.encode_cell({ r: row, c: col });
      const cell = sheet[cellRef];
      if (!cell) continue;

      if (cell.t === 'n') {
        cell.z = EXPORT_INTEGER_NUMBER_FORMAT;
        cell.s = {
          ...cell.s,
          numFmt: EXPORT_INTEGER_NUMBER_FORMAT,
          alignment: {
            horizontal: 'right',
            vertical: 'center',
            wrapText: false,
          },
        };
        continue;
      }

      cell.s = {
        ...cell.s,
        alignment: {
          horizontal: 'left',
          vertical: 'center',
          wrapText: false,
        },
      };
    }
  }

  sheet['!cols'] = headers.map((header) => ({ wch: getColumnWidth(header) }));
  sheet['!autofilter'] = { ref: sheet['!ref'] };

  if (options?.freezePanes !== false) {
    // Freeze header row + first two columns (Element name, Resource name)
    sheet['!views'] = [
      {
        state: 'frozen',
        xSplit: 2,
        ySplit: 1,
        topLeftCell: 'C2',
        activePane: 'bottomRight',
      },
    ];
  }
};

/**
 * Applies thousand-separator integer formatting to every numeric cell on {@link sheet}.
 * Prefer {@link applyExportSheetPresentation} for resource sheets.
 */
const applyExportIntegerNumberFormats = (sheet: WorkSheet): void => {
  if (!sheet['!ref']) return;
  const range = utils.decode_range(sheet['!ref']);
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = utils.encode_cell({ r: row, c: col });
      const cell = sheet[cellRef];
      if (!cell || cell.t !== 'n') continue;
      cell.z = EXPORT_INTEGER_NUMBER_FORMAT;
      cell.s = {
        ...cell.s,
        numFmt: EXPORT_INTEGER_NUMBER_FORMAT,
        alignment: {
          horizontal: 'right',
          vertical: 'center',
          wrapText: false,
        },
      };
    }
  }
};

type ExportOverviewPresentationOptions = {
  metadataRowCount: number;
  /** 0-based row index of the proposal summary header. */
  headerRow: number;
  headerColumnCount: number;
  /** Total AOA rows including metadata, spacer, header, and data. */
  rowCount: number;
  columnWidths: readonly number[];
};

/**
 * Formats the Overview sheet: number formats, alignment, bold metadata/header,
 * column widths, autofilter on the proposal table, and frozen panes.
 */
export const applyExportOverviewPresentation = (
  sheet: WorkSheet,
  options: ExportOverviewPresentationOptions,
): void => {
  applyExportIntegerNumberFormats(sheet);

  if (sheet['!ref']) {
    const overviewRange = utils.decode_range(sheet['!ref']);
    for (let row = overviewRange.s.r; row <= overviewRange.e.r; row++) {
      for (let col = overviewRange.s.c; col <= overviewRange.e.c; col++) {
        const cellRef = utils.encode_cell({ r: row, c: col });
        const cell = sheet[cellRef];
        if (!cell) continue;
        const isNumber = cell.t === 'n';
        cell.s = {
          ...cell.s,
          alignment: {
            horizontal: isNumber ? 'right' : 'left',
            vertical: 'center',
            wrapText: false,
          },
        };
      }
    }
  }

  for (let row = 0; row < options.metadataRowCount; row++) {
    const cellRef = utils.encode_cell({ r: row, c: 0 });
    if (!sheet[cellRef]) continue;
    sheet[cellRef].s = {
      ...sheet[cellRef].s,
      font: { bold: true },
    };
  }

  for (let col = 0; col < options.headerColumnCount; col++) {
    const cellRef = utils.encode_cell({ r: options.headerRow, c: col });
    if (!sheet[cellRef]) continue;
    sheet[cellRef].s = {
      ...sheet[cellRef].s,
      font: { bold: true },
      fill: {
        patternType: 'solid',
        fgColor: { rgb: EXPORT_HEADER_FILL_COLOR },
      },
    };
  }

  sheet['!cols'] = options.columnWidths.map((wch) => ({ wch }));

  // Filter only the proposal summary table (header + rows)
  if (options.rowCount > options.headerRow + 1) {
    const lastRow = options.rowCount - 1;
    sheet['!autofilter'] = {
      ref: utils.encode_range({
        s: { r: options.headerRow, c: 0 },
        e: { r: lastRow, c: options.headerColumnCount - 1 },
      }),
    };
  }

  sheet['!views'] = [
    {
      state: 'frozen',
      xSplit: 1,
      ySplit: options.headerRow + 1,
      topLeftCell: `B${options.headerRow + 2}`,
      activePane: 'bottomRight',
    },
  ];
};

/**
 * Sums lifecycle-filtered CO2e (kg) for exportable product rows in a proposal scenario.
 */
export const sumExportableProductCo2eKg = (
  resultsRecord: ResultsRecord,
  productElements: IProductElement[],
  selectedLifecycles: Lifecycle[],
): number =>
  productElements.reduce((total, element) => {
    const factors = resultsRecord[element.id];
    if (!factors) return total;
    const filtered = filterResultsByLifecycles(factors, selectedLifecycles);
    return total + getConversionFactorValue(filtered, 'co2e');
  }, 0);

/**
 * Product elements to include on a resource worksheet.
 * Pass {@link pathVersion} when the caller already resolved the proposal tree
 * so export does not resolve it twice.
 */
export const getExportSpreadsheetProductElements = (
  version: Baseline,
  resultsRecord: ResultsRecord,
  proposal?: IProposal,
  pathVersion: Baseline = proposal
    ? getResolvedBuildingVersion(version, proposal)
    : version,
): IProductElement[] => {
  const byId = new Map<string, IProductElement>();

  for (const element of getElementsInProposal(version, proposal)) {
    if (isProductElement(element)) byId.set(element.id, element);
  }

  forEachElement(pathVersion, (element, path) => {
    if (!isProductElement(element) || byId.has(element.id)) return;
    if (!isElementActiveInProposal(proposal, path, element)) return;
    const factors = resultsRecord[element.id];
    if (!factors || getConversionFactorValue(factors, 'co2e') === 0) return;
    byId.set(element.id, element);
  });

  return [...byId.values()];
};

/**
 * Builds one resource row for a product element.
 */
const buildExportResourceRow = (
  element: IProductElement,
  version: Baseline,
  filteredFactors: Results,
  lifecycleColumns: ExportLifecycleColumns,
  pathVersion: Baseline,
  marketEstimates?: ProjectMarketEstimates,
): Record<string, ExportCellValue> => {
  const path = getPathToElement(pathVersion, element).filter(isElement);
  const productCategoryElement = path.filter(isProductCategoryElement)[0];
  const systemCategoryElement = last(path.filter(isSystemCategoryElement));
  const sbefCode = productCategoryElement
    ? getSbefCodeFromElementOrRecipe(productCategoryElement)
    : undefined;
  const climate = getExportClimateFields(element, version, marketEstimates);
  const product = version.products[element.product_id];
  const massOrEnergyUnit = getExportMassOrEnergyUnit(element);

  const row: Record<string, ExportCellValue> = {
    [EXPORT_COLUMN.elementName]: getElementName(systemCategoryElement) || '',
    [EXPORT_COLUMN.resourceName]: getElementName(productCategoryElement) || '',
    [EXPORT_COLUMN.massKg]: getOptionalRoundedFactor(
      filteredFactors,
      massOrEnergyUnit,
    ),
    [EXPORT_COLUMN.co2eTotal]: getOptionalRoundedFactor(
      filteredFactors,
      'co2e',
    ),
    [EXPORT_COLUMN.costTotal]: getOptionalRoundedFactor(filteredFactors, 'sek'),
    [EXPORT_COLUMN.climateData]: climate.climateData,
    [EXPORT_COLUMN.dataSource]: climate.dataSource,
    [EXPORT_COLUMN.sourceId]: '',
    [EXPORT_COLUMN.epdDescription]: getProductDescription(element, version),
    [EXPORT_COLUMN.sbefParent]: getExportSbefParent(sbefCode),
    [EXPORT_COLUMN.sbefCode]: getSbefLabel(sbefCode, { fallback: '' }),
    [EXPORT_COLUMN.boverketCategory]: '',
  };

  for (const column of lifecycleColumns.all) {
    row[column.header] = getOptionalRoundedFactor(filteredFactors, column.unit);
  }

  if (product) {
    const genericProduct = product.generic_id
      ? getGenericProduct(version, product.generic_id)
      : undefined;
    row[EXPORT_COLUMN.sourceId] = getOriginalProductId(product);
    row[EXPORT_COLUMN.boverketCategory] = getProductCategories(
      genericProduct ?? product,
      'Boverket',
    ).join(', ');
  }

  return row;
};

/**
 * Builds the building-level row for {@link buildingLevelResults} (B7 water use, C1 base
 * demolition energy, C3-C4 BD7/BD8 schablon). These stages have no cost dimension and no
 * mass, so those cells stay blank.
 *
 * Returns `undefined` when the selected scope contains no building-level value, so the
 * row is only added to sheets where it means something.
 */
export const buildExportBuildingLevelRow = (
  buildingLevelResults: Results,
  lifecycleColumns: ExportLifecycleColumns,
  selectedLifecycles: Lifecycle[],
): Record<string, ExportCellValue> | undefined => {
  const filteredFactors = filterResultsByLifecycles(
    buildingLevelResults,
    selectedLifecycles,
  );
  const hasSelectedStageValue = lifecycleColumns.co2e.some((column) =>
    hasConversionFactor(filteredFactors, column.unit),
  );
  if (!hasSelectedStageValue) return undefined;

  const row: Record<string, ExportCellValue> = {
    [EXPORT_COLUMN.elementName]: EXPORT_BUILDING_LEVEL_ELEMENT_LABEL,
    [EXPORT_COLUMN.resourceName]: EXPORT_BUILDING_LEVEL_RESOURCE_LABEL,
    [EXPORT_COLUMN.co2eTotal]: Math.round(
      getConversionFactorValue(filteredFactors, 'co2e'),
    ),
  };

  for (const column of lifecycleColumns.co2e) {
    row[column.header] = getOptionalRoundedFactor(filteredFactors, column.unit);
  }

  return row;
};

export type VersionToSheetParams = {
  version: Baseline;
  resultsRecord: ResultsRecord;
  selectedLifecycles: Lifecycle[];
  elements: IProductElement[];
  /** Resolved proposal tree for path lookups; defaults to {@link version}. */
  pathVersion?: Baseline;
  marketEstimates?: ProjectMarketEstimates;
  /**
   * Results belonging to the building rather than any element, appended as a final row.
   * Omit to export resource rows only.
   */
  buildingLevelResults?: Results;
};

/**
 * Builds one resource worksheet for a baseline or proposal scenario.
 */
export const versionToSheet = ({
  version,
  resultsRecord,
  selectedLifecycles,
  elements,
  pathVersion = version,
  marketEstimates,
  buildingLevelResults,
}: VersionToSheetParams): WorkSheet => {
  const lifecycleColumns = getExportLifecycleColumns(selectedLifecycles);
  const headers = getExportResourceHeaders(lifecycleColumns);
  /** Blank out missing cells so every row keeps the header order for json_to_sheet. */
  const toOrderedRow = (row: Record<string, ExportCellValue>) =>
    Object.fromEntries(headers.map((header) => [header, row[header] ?? null]));

  const resourceRows = elements.flatMap((element) => {
    const factors = resultsRecord[element.id];
    if (!factors) return [];

    const filteredFactors = filterResultsByLifecycles(
      factors,
      selectedLifecycles,
    );
    return [
      toOrderedRow(
        buildExportResourceRow(
          element,
          version,
          filteredFactors,
          lifecycleColumns,
          pathVersion,
          marketEstimates,
        ),
      ),
    ];
  });

  const buildingLevelRow = buildingLevelResults
    ? buildExportBuildingLevelRow(
        buildingLevelResults,
        lifecycleColumns,
        selectedLifecycles,
      )
    : undefined;
  const rows = buildingLevelRow
    ? [...resourceRows, toOrderedRow(buildingLevelRow)]
    : resourceRows;

  const sheet =
    rows.length > 0
      ? utils.json_to_sheet(rows, { cellStyles: true, skipHeader: false })
      : utils.aoa_to_sheet([headers]);

  applyExportSheetPresentation(sheet, headers);
  return sheet;
};
